import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle2, Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { mockData } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Hero = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/activities`);
      // Get only the first 3 published activities
      setActivities(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const highlights = [
    'Registered since 2021',
    'Multi-disciplinary expertise',
    'Sustainable solutions'
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 overflow-hidden bg-gradient-to-br from-[#176472]/5 via-white to-[#74C65A]/5">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-[#74C65A]/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#176472]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      {/* Animated circles */}
      <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-[#74C65A] rounded-full animate-ping opacity-75"></div>
      <div className="absolute bottom-1/3 right-1/3 w-3 h-3 bg-[#1E4E9E] rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }}></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block animate-fade-in">
              <span className="bg-gradient-to-r from-[#74C65A] to-[#003366] text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
                Established {mockData.company.established}
              </span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 leading-tight">
              {mockData.company.name}
              <span className="block mt-3 bg-gradient-to-r from-[#003366] via-[#339933] to-[#003366] bg-clip-text text-transparent animate-fade-in">
                {mockData.company.tagline}
              </span>
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
              Empowering Timor-Leste through innovation and sustainable solutions, Elzalira delivers integrated engineering for a smarter, more resilient future. We bring together technology, energy, and infrastructure with a commitment to excellence, ensuring that every project contributes to long-term national development and community well-being.
            </p>

            {/* Highlights */}
            <div className="flex flex-wrap gap-4">
              {highlights.map((highlight, index) => (
                <div key={index} className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                  <CheckCircle2 className="text-[#74C65A] w-5 h-5" />
                  <span className="text-sm font-medium text-slate-700">{highlight}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection('services')}
                size="lg"
                className="bg-gradient-to-r from-[#003366] to-[#339933] hover:from-[#339933] hover:to-[#003366] text-white transition-all duration-500 transform hover:scale-110 shadow-xl hover:shadow-2xl"
              >
                Explore Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="border-2 border-[#003366] text-[#003366] bg-white hover:bg-gradient-to-r hover:from-[#1E4E9E] hover:to-[#00B050] hover:text-white hover:border-transparent transition-all duration-500 transform hover:scale-110 shadow-lg"
              >
                Contact Us
              </Button>
            </div>
          </div>

          {/* Right Content - Activities/Events Section */}
          <div className="relative">
            <Card className="relative z-10 bg-white/90 backdrop-blur-md shadow-2xl border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-[#003366] to-[#339933] p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Ongoing Activities</h3>
                    <p className="text-white/90 text-sm">Current projects and upcoming events</p>
                  </div>
                </div>
              </div>

              <CardContent className="p-6 space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500">Loading activities...</p>
                  </div>
                ) : activities.length > 0 ? (
                  activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:shadow-md transition-all duration-300 hover:scale-105"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-slate-900 text-base">{activity.title}</h4>
                        <span className="text-xs px-2 py-1 rounded-full font-semibold bg-green-100 text-green-700">
                          {activity.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      {activity.image && (
                        <img
                          src={`${BACKEND_URL}${activity.image}`}
                          alt={activity.title}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      <p className="text-sm text-slate-600 mb-2 line-clamp-2">{activity.content}</p>
                      <div className="flex items-center text-sm text-slate-600">
                        <Clock className="w-4 h-4 text-[#74C65A] mr-1" />
                        <span>{activity.date}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500">No activities available at the moment.</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#00B050] rounded-full animate-pulse opacity-20"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#1E4E9E] rounded-full animate-pulse opacity-20" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </div>
    </section>
  );
};
